import type { WorkflowRunEvent } from "@/types/workflowEngine";

/**
 * Phase 4A.8 — derive per-node visual status from the append-only
 * workflow_run_events stream. Pure + unit-tested (runStatusPure.test.ts).
 */

export type NodeRunStatus = "running" | "completed" | "failed" | "waiting" | "skipped";

export interface NodeRunInfo {
  status: NodeRunStatus;
  durationMs?: number | null;
  /** last output snapshot for this node (crew: last delegation result) */
  outputSnapshot?: unknown;
  /** number of completed events (crew nodes emit one per delegation) */
  completions: number;
}

const STATUS_BY_EVENT: Record<string, NodeRunStatus | undefined> = {
  started: "running",
  completed: "completed",
  failed: "failed",
  paused: "waiting",
  resumed: "running",
  skipped: "skipped",
};

/**
 * Events are ordered ascending; the LAST status-bearing event per node wins.
 * A "started" with a later "completed"/"failed" resolves to that terminal
 * state; a node whose last event is "started" is running right now.
 */
export function deriveNodeStatuses(events: WorkflowRunEvent[]): Record<string, NodeRunInfo> {
  const map: Record<string, NodeRunInfo> = {};
  for (const e of events) {
    if (!e.nodeId) continue;
    const status = STATUS_BY_EVENT[e.eventType];
    if (!status) continue; // fork/join/artifact/etc. don't change node status
    const prev = map[e.nodeId];
    map[e.nodeId] = {
      status,
      durationMs: e.durationMs ?? prev?.durationMs ?? null,
      outputSnapshot: e.outputSnapshot ?? prev?.outputSnapshot,
      completions: (prev?.completions ?? 0) + (e.eventType === "completed" ? 1 : 0),
    };
  }
  return map;
}

/** True while the run should keep polling (active or paused-for-human). */
export function isRunActive(runStatus: string | undefined | null): boolean {
  return runStatus === "running" || runStatus === "pending" || runStatus === "waiting_approval";
}

export const STATUS_COLORS: Record<NodeRunStatus, string> = {
  running: "#2563eb",
  completed: "#16a34a",
  failed: "#dc2626",
  waiting: "#d97706",
  skipped: "#6b7280",
};
